import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listScooters } from "@/lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function Scooters() {
    const [scooters, setScooters] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        listScooters()
            .then(setScooters)
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="scooters-page" className="mx-auto max-w-7xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
                <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                    Our Fleet
                </div>
                <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                    Rent a mobility scooter
                </h1>
                <p className="mt-5 max-w-2xl text-slate-600">
                    Pick the model that fits your trip. All scooters are
                    delivered fully charged and ready to roll.
                </p>
            </div>

            <div className="mt-12 grid gap-8 md:grid-cols-2">
                {loading && (
                    <div className="card-surface h-64 animate-pulse bg-slate-100" />
                )}
                {scooters.map((s) => (
                    <Link
                        to={`/scooters/${s.id}`}
                        key={s.id}
                        data-testid={`scooter-card-${s.id}`}
                        className="card-surface group flex flex-col overflow-hidden"
                    >
                        <div className="aspect-[16/10] w-full overflow-hidden bg-slate-100">
                            <img
                                src={s.image}
                                alt={s.name}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>
                        <div className="flex flex-1 flex-col p-6 sm:p-8">
                            <h3 className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                                {s.name}
                            </h3>
                            <p className="mt-2 text-sm text-slate-600">
                                {s.tagline}
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-slate-700">
                                {s.features.slice(0, 3).map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2"
                                    >
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-green)]" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 flex items-end justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">
                                    From
                                </div>
                                <div className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                                    ${s.tiers[1].toFixed(0)}
                                    <span className="ml-1 text-sm font-medium text-slate-500">
                                        /day
                                    </span>
                                </div>
                            </div>
                                <span className="hero-cta pill-btn inline-flex items-center gap-2 text-white">
                                    Book
                                    <ArrowRight className="h-4 w-4" />
                                </span>
                            </div>
                        </div>
                    </Link>
                ))}
                <div className="card-surface flex flex-col items-start justify-center p-8 text-slate-500">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-gold)]">
                        Coming soon
                    </div>
                    <h3 className="mt-2 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                        More scooters &amp; wheelchairs
                    </h3>
                    <p className="mt-2 text-sm">
                        We&apos;re expanding our fleet. Let us know what you
                        need and we&apos;ll be in touch.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="scooters-suggest-link"
                        className="mt-5 text-sm font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-green)]"
                    >
                        Suggest a model →
                    </Link>
                </div>
            </div>
        </div>
    );
}
